import * as Timing from './PerformanceBar/Timing';
import {
  EventType,
  IMappedPerformanceEntry,
  INavigationEvent,
  IPerformanceService,
  IPerformanceTimingData,
  IRouteChangeEvent,
  IRouteChangeEventDetail,
  IScenarioTelemetryData,
  ISplitTiming,
} from './PerformanceService.types';

// TODO: could use some refactoring/cleanup, triggers a variety of linter warnings + errors
/* eslint-disable i18next/no-literal-string */
class PerformanceService implements IPerformanceService {
  // Timestamp at which the last navigation occurred via page refresh or client-side navigation
  private navigationStartTime: number;

  private initialPageNavigationStartTime: number;

  private navigationSplitTimings: ISplitTiming[];

  // Indicates that a entire page was refreshed. It's false if page was navigated by link and
  // true if page was reloaded by entering the URL in the browser's address bar.
  private isPageRefreshed: boolean;

  public constructor() {
    this.isPageRefreshed = true;
    // window may not be defined in test environment:
    this.navigationStartTime = window?.performance?.timing?.navigationStart || Date.now();
    this.initialPageNavigationStartTime = this.navigationStartTime;
    this.navigationSplitTimings = [];

    // handle browser back/forward events
    window.addEventListener('popstate', this.onPopState);

    // monitor route changes so that we can update navigation start time
    document.body.addEventListener(EventType.RouteChange, this.onRouteChange);
  }

  public getTTIData(
    additionalProperties?: { [name: string]: string },
    endTime?: number
  ): { timingData: IPerformanceTimingData; logProperties: { [name: string]: string | number | boolean } } {
    const isPageRefreshScenario = this.isPageRefreshScenario();
    const navigationStartTime = this.getNavigationStartTime();
    const duration = (endTime || Date.now()) - navigationStartTime;

    const scenarioTelemetryData: IScenarioTelemetryData = {
      backgroundTab: document.hidden,
      elapsedTime: duration,
      pageRefreshScenario: isPageRefreshScenario,
      timeZoneOffset: new Date().getTimezoneOffset(),
    };

    const properties: { [name: string]: string | number | boolean } = {
      ...scenarioTelemetryData,
      ...additionalProperties,
      Duration: scenarioTelemetryData.elapsedTime, // https://microsoft.sharepoint.com/:w:/r/teams/scweb/Shared%20Documents/Project%20Gibraltar/Telemetry/Scuba/Scuba%20PageLoad%20Dashboard.docx?d=w0117455a746d4daebb4fb255310a6aea&csf=1&web=1&e=vuGvbK
    };

    const performanceData: IPerformanceTimingData = {
      navigationStartTime: this.navigationStartTime,
      pageLoadTime: duration,
      splitTimings: this.navigationSplitTimings,
    };

    if (isPageRefreshScenario) {
      try {
        // Best-effort attempt to add page interactive properties.
        const interactiveProperties = this.getPageInteractiveTelemetryProperties();
        Object.assign(properties, interactiveProperties);

        const navigationTimings = interactiveProperties.navigation;
        performanceData.preRequestTime = navigationTimings.PreRequest.duration;
        performanceData.fetchTime = navigationTimings.Fetch.duration;
        performanceData.DNSTime = navigationTimings.DNS.duration;
        performanceData.TCPTime = navigationTimings.TCP.duration;
        performanceData.requestTime = navigationTimings.Request.duration;
        performanceData.responseTime = navigationTimings.Response.duration;
        performanceData.serverTime = navigationTimings['Server Time'].duration;
      } catch {
        // do nothing
      }
    }

    return { timingData: performanceData, logProperties: properties };
  }

  public addSplitTiming(name: string): void {
    const splitTiming: ISplitTiming = {
      name,
      timestamp: Date.now(),
    };
    this.navigationSplitTimings = [...this.navigationSplitTimings, splitTiming];
  }

  public async measure<R>(name: string, fn: () => Promise<R>): Promise<R> {
    this.startMeasurement(name);
    const startMarkName = `Start[${name}]`;
    const endMarkName = `End[${name}]`;
    performance.mark(startMarkName);

    try {
      return await fn();
    } finally {
      this.endMeasurement(name);
      performance.mark(endMarkName);
      performance.measure(name, startMarkName, endMarkName);
    }
  }

  public startMeasurement(name: string): void {
    const splitTiming: ISplitTiming = {
      name: `Start[${name}]`,
      timestamp: Date.now(),
    };
    this.navigationSplitTimings = [...this.navigationSplitTimings, splitTiming];
  }

  public endMeasurement(name: string): void {
    const splitTiming: ISplitTiming = {
      name: `End[${name}]`,
      timestamp: Date.now(),
    };

    const startTimingName = `Start[${name}]`;
    const startTiming = this.navigationSplitTimings.find((t) => t.name === startTimingName);
    if (startTiming) {
      splitTiming.duration = splitTiming.timestamp - startTiming.timestamp;
    }

    this.navigationSplitTimings = [...this.navigationSplitTimings, splitTiming];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getPageInteractiveTelemetryProperties(): { [key: string]: any } {
    const timingDetails: { [key: string]: { start: number; duration: number } } = {};
    const timings: { [key: string]: number } = {};
    const properties: { [key: string]: any } = {}; // eslint-disable-line @typescript-eslint/no-explicit-any

    if (this.isPageRefreshScenario()) {
      const navigationEvents: INavigationEvent[] = this.getDefaultNavigationEvents();

      for (let i = 0; i < navigationEvents.length; i++) {
        const { perfEntry } = navigationEvents[i];
        if (perfEntry) {
          timingDetails[perfEntry.name] = {
            start: perfEntry.startTime,
            duration: perfEntry.duration,
          };

          timings[perfEntry.name] = perfEntry.duration;
        }
      }

      properties.navigation = timingDetails;

      // Set flatten timings for Scuba queries
      Object.assign(properties, timings);
    }

    // best effort attempt to add window size and connection properties
    try {
      properties.screen = {
        w: window.screen.width,
        h: window.screen.height,
        winW: window.innerWidth,
        winH: window.innerHeight,
        winOW: window.outerWidth,
        winOH: window.outerHeight,
      };

      const { navigator } = window as Window;

      if (navigator?.connection) {
        const { rtt, effectiveType: type, downlink } = navigator.connection;

        properties.connection = {
          rtt,
          type,
          downlink,
        };
      }
    } catch {
      // do nothing
    }

    return properties;
  }

  // eslint-disable-next-line class-methods-use-this
  private getDefaultNavigationEvents(): INavigationEvent[] {
    const events: INavigationEvent[] = [];

    try {
      const expectedDefaultEvents: INavigationEvent[] = [
        { name: 'PLT', startEvent: 'navigationStart', endEvent: 'loadEventEnd', isAggregate: true },
        { name: 'Redirect', startEvent: 'redirectStart', endEvent: 'redirectEnd' },
        { name: 'Unload', startEvent: 'unloadEventStart', endEvent: 'unloadEventEnd' },
        { name: 'Fetch', startEvent: 'fetchStart', endEvent: 'domainLookupStart' },
        { name: 'DNS', startEvent: 'domainLookupStart', endEvent: 'domainLookupEnd' },
        { name: 'TCP', startEvent: 'connectStart', endEvent: 'connectEnd' },
        { name: 'SSL', startEvent: 'secureConnectionStart', endEvent: 'connectEnd' },
        // Use connectEnd instead of requestStart for request/server time timings, to work around IE11 issue with requestStart
        { name: 'Request', startEvent: 'connectEnd', endEvent: 'responseStart' },
        { name: 'Server Time', startEvent: 'connectEnd', endEvent: 'responseEnd', isAggregate: true },
        { name: 'Response', startEvent: 'responseStart', endEvent: 'responseEnd' },
        { name: 'DOMInteractive', startEvent: 'domInteractive', endEvent: 'domComplete' },
        { name: 'DOMProcessing', startEvent: 'domLoading', endEvent: 'domComplete' },
        { name: 'onLoad', startEvent: 'loadEventStart', endEvent: 'loadEventEnd' },
        { name: 'Network Time', startEvent: 'domainLookupStart', endEvent: 'connectEnd' },
        { name: 'PreRequest', startEvent: 'navigationStart', endEvent: 'connectEnd', isAggregate: true },
      ];

      // eslint-disable-next-line no-restricted-syntax
      for (const expectedDefaultEvent of expectedDefaultEvents) {
        let entries: PerformanceEntry[] = Timing.getEntriesByName(expectedDefaultEvent.name);

        if (entries.length === 0) {
          // The event has not been measured yet. Make sure both the start and end events have been triggered.
          // For some marks, this is not a timing, it's an entry, so check for presence in both
          if (
            (Timing.getTimingByName(expectedDefaultEvent.startEvent) == null &&
              Timing.getEntriesByName(expectedDefaultEvent.startEvent).length === 0) ||
            (Timing.getTimingByName(expectedDefaultEvent.endEvent) == null &&
              Timing.getEntriesByName(expectedDefaultEvent.endEvent).length === 0)
          ) {
            // At least one of the requested timings has not been recorded yet, skip this event.
            continue; // eslint-disable-line no-continue
          }

          Timing.measure(expectedDefaultEvent.name, expectedDefaultEvent.startEvent, expectedDefaultEvent.endEvent);
          entries = Timing.getEntriesByName(expectedDefaultEvent.name);
        }

        // Create deep copies of object to work around Chrome bug where PerformanceEntry stopped having a serializer
        // (serializer is not required by W3C spec).
        const newEntry = entries.map(
          ({ name, startTime, duration }) =>
            ({
              name,
              startTime,
              duration,
            }) as IMappedPerformanceEntry
        )[0];

        expectedDefaultEvent.perfEntry = newEntry;
        events.push(expectedDefaultEvent);
      }
    } catch (error) {
      console.error(`Unexpected error: ${error}`);
    }

    return events;
  }

  public getNavigationStartTime(): number {
    return this.navigationStartTime;
  }

  public getElapsedTimeInMinutes(): number {
    const elapsedTime = Date.now() - this.initialPageNavigationStartTime;
    return elapsedTime / (1000 * 60);
  }

  public resetPageLoadState() {
    this.resetNavigationState();
  }

  private isPageRefreshScenario(): boolean {
    return this.isPageRefreshed;
  }

  private onRouteChange = (event: Event): void => {
    const routeEvent = event as IRouteChangeEvent<IRouteChangeEventDetail>;
    this.resetNavigationState(routeEvent.detail.startTime);
  };

  private onPopState = () => {
    this.resetNavigationState();
  };

  private resetNavigationState(navigationStartTime?: number) {
    this.isPageRefreshed = false;
    this.navigationStartTime = navigationStartTime || Date.now();
    this.navigationSplitTimings = [];
  }
}

export const performanceService: IPerformanceService = new PerformanceService();
